import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

interface PasswordVerificationModalProps {
  open: boolean;
  onClose: () => void;
  onVerify: (password: string) => Promise<boolean>;
  onVerified?: () => void;
  title?: string;
  description?: string;
}

const PasswordVerificationModal: React.FC<PasswordVerificationModalProps> = ({
  open,
  onClose,
  onVerify,
  onVerified,
  title = "Password Verification Required",
  description = "Please enter your password to continue accessing this sensitive area."
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Nautilus Console Design System Tokens
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await onVerify(password);
      if (success) {
        setPassword('');
        onVerified?.();
        onClose();
      } else {
        setError('Incorrect password. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setError(null);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: surfaceBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
        }
      }}
      aria-labelledby="password-verification-title"
    >
      <DialogTitle
        id="password-verification-title"
        sx={{
          borderBottom: `1px solid ${panelBorder}`,
          py: 1.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08),
          }}
        >
          <LockIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />
        </Box>
        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <Box component="form" noValidate onSubmit={handleSubmit}>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ mb: 3, color: isDark ? '#b0bec5' : '#546e7a' }}
          >
            {description}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 1 }}
            >
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            label="Password"
            type="password"
            fullWidth
            required
            value={password}
            error={Boolean(error)}
            onChange={(e) => {
              setError(null);
              setPassword(e.target.value);
            }}
            disabled={loading}
            aria-describedby="password-helper"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1
              }
            }}
          />
          <Typography id="password-helper" variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Enter your current password to verify your identity
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              borderRadius: 1,
              px: 3,
              color: 'text.secondary',
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !password.trim()}
            sx={{
              borderRadius: 1,
              px: 3,
              fontWeight: 600,
              bgcolor: NAUTILUS.sand,
              color: NAUTILUS.deepOcean,
              '&:hover': {
                bgcolor: alpha(NAUTILUS.sand, 0.85),
              },
              '&:disabled': {
                bgcolor: alpha(NAUTILUS.sand, 0.3),
              },
            }}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PasswordVerificationModal;